import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import DocumentManager from '../components/DocumentManager';
import { 
  LogOut, 
  Download, 
  Eye, 
  CheckCircle, 
  XCircle, 
  Clock,
  Mail,
  Phone,
  User,
  Trash2,
  Link2,
  Copy,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [applications, setApplications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [eventName, setEventName] = useState('');
  const navigate = useNavigate();
  const { eventId } = useParams();

  useEffect(() => {
    // Nastav název akce podle eventId
    const eventNames = {
      'kontakt-fest-2026': 'KONTAKT Fest 2026',
      'cesta-kolem-sveta-2026': 'Gastrofestival Cesta Kolem Světa',
      'otrokovicke-letni-slavnosti-2026': 'Otrokovické letní slavnosti',
      'festival-koupacek-2026': 'Festival Koupáček',
      'burger-rock-fest-2026': 'Burger Rock Fest Napajedla',
      'majofest-otrokovice-2026': 'Májofest Otrokovice',
      'den-zk-2026': 'Den Zlínského kraje'
    };
    
    setEventName(eventNames[eventId] || eventId.replace(/-/g, ' ').toUpperCase());
    
    loadApplications();
  }, [eventId]);

  const loadApplications = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      
      if (!token) {
        navigate('/admin/login');
        return;
      }

      console.log('🔍 Načítám přihlášky pro eventId:', eventId);

      // Přidej eventId jako query parametr
      const response = await axios.get(`${BACKEND_URL}/api/vendor-applications?event_id=${eventId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('✅ Načteno přihlášek:', response.data.length);
      console.log('📋 EventId přihlášek:', response.data.map(app => ({ name: app.vendorName, eventId: app.eventId })));

      setApplications(response.data);
      
      // Pokud je otevřený detail, refreshni ho také
      if (selectedApp) {
        const updatedApp = response.data.find(app => app.id === selectedApp.id);
        if (updatedApp) {
          setSelectedApp(updatedApp);
        }
      }
    } catch (error) {
      if (error.response?.status === 401) {
        // Vyčistit localStorage při 401 chybě
        localStorage.removeItem('adminToken');
        toast.error('Session vypršela, přihlaste se znovu');
        navigate('/admin/login');
      } else {
        toast.error('Chyba při načítání přihlášek');
      }
      console.error('Error loading applications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    toast.success('Odhlášení úspěšné');
    navigate('/admin/login');
  };

  const handleExportCSV = () => {
    const headers = [
      'ID', 'Datum', 'Název prodejce', 'Jméno', 'Email', 'Telefon', 
      'WWW', 'Plátce DPH', 'Rozměr stánku', 'Elektrická přípojka', 'Status'
    ];

    const rows = applications.map(app => [
      app.id,
      new Date(app.createdAt).toLocaleDateString('cs-CZ'),
      app.vendorName,
      app.name,
      app.email,
      app.phone,
      app.website,
      app.vatPayer,
      app.stallSize,
      app.electricConnection,
      app.status
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `prihlasty-kontakt-fest-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();

    toast.success('CSV soubor stažen');
  };

  const handleExportExcel = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      
      const response = await axios.get(
        `${BACKEND_URL}/api/vendor-applications/export/approved-excel?event_id=${eventId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          responseType: 'blob'
        }
      );

      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${eventId}_schvaleni_prodejci.xlsx`;
      link.click();

      toast.success('Excel soubor stažen');
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Žádné schválené přihlášky k exportu');
      } else {
        toast.error('Chyba při exportu Excel souboru');
      }
      console.error('Error exporting Excel:', error);
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      
      await axios.patch(
        `${BACKEND_URL}/api/vendor-applications/${id}/status?status=${newStatus}`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      toast.success(`Status změněn na ${newStatus}`);
      loadApplications();
    } catch (error) {
      toast.error('Chyba při změně statusu');
      console.error('Error updating status:', error);
    }
  };

  const updatePaymentStatus = async (id, newPaymentStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      
      await axios.patch(
        `${BACKEND_URL}/api/vendor-applications/${id}/payment-status?payment_status=${newPaymentStatus}`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      toast.success(`Stav platby změněn na ${newPaymentStatus === 'paid' ? 'Zaplaceno' : 'Nezaplaceno'}`);
      loadApplications();
    } catch (error) {
      toast.error('Chyba při změně stavu platby');
      console.error('Error updating payment status:', error);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };

    const icons = {
      pending: Clock,
      approved: CheckCircle,
      rejected: XCircle
    };

    const labels = {
      pending: 'Čeká',
      approved: 'Schváleno',
      rejected: 'Zamítnuto'
    };

    const Icon = icons[status];

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${styles[status]}`}>
        <Icon className="w-4 h-4" />
        {labels[status]}
      </span>
    );
  };

  const getPaymentStatusBadge = (paymentStatus) => {
    const isPaid = paymentStatus === 'paid';
    
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
        isPaid ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
      }`}>
        {isPaid ? '💰 Zaplaceno' : '⏳ Nezaplaceno'}
      </span>
    );
  };

  const handleDelete = async (id, vendorName) => {
    try {
      const token = localStorage.getItem('adminToken');
      
      await axios.delete(
        `${BACKEND_URL}/api/vendor-applications/${id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      toast.success(`Přihláška "${vendorName}" byla smazána`);
      setDeleteConfirm(null);
      setSelectedApp(null);
      loadApplications();
    } catch (error) {
      toast.error('Chyba při mazání přihlášky');
      console.error('Error deleting application:', error);
    }
  };

  const copyStatusLink = (id, vendorName) => {
    const statusUrl = `${window.location.origin}/status/${id}`;
    navigator.clipboard.writeText(statusUrl).then(() => {
      toast.success(`Link pro "${vendorName}" zkopírován do schránky`);
    }).catch(() => {
      toast.error('Nepodařilo se zkopírovat link');
    });
  };

  const openStatusPage = (id) => {
    const statusUrl = `${window.location.origin}/status/${id}`;
    window.open(statusUrl, '_blank');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Načítám přihlášky...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/admin/events')}
              variant="outline"
              size="sm"
              className="border-2"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zpět na výběr akcí
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Admin Dashboard</h1>
              <p className="text-sm text-gray-600">{eventName} - Správa přihlášek</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => navigate('/admin/overview')}
              variant="outline"
              className="border-2 border-orange-300 hover:bg-orange-50"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zpět na přehled
            </Button>
            <Button
              onClick={async () => {
                try {
                  const token = localStorage.getItem('adminToken');
                  const response = await axios.post(`${BACKEND_URL}/api/backup/create-backup`, {}, {
                    headers: { 'Authorization': `Bearer ${token}` }
                  });
                  toast.success(`Záloha vytvořena: ${response.data.applications_count} přihlášek`);
                } catch (error) {
                  toast.error('Chyba při vytváření zálohy');
                }
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Vytvořit zálohu
            </Button>
            <Button
              onClick={handleExportExcel}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Excel - Schválení
            </Button>
            <Button
              onClick={handleExportCSV}
              variant="outline"
              className="border-2"
            >
              <Download className="w-4 h-4 mr-2" />
              CSV - Všichni
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-2"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Odhlásit se
            </Button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="text-3xl font-bold text-orange-500">{applications.length}</div>
              <div className="text-sm text-gray-600">Celkem přihlášek</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="text-3xl font-bold text-yellow-500">
                {applications.filter(a => a.status === 'pending').length}
              </div>
              <div className="text-sm text-gray-600">Čeká na schválení</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="text-3xl font-bold text-green-500">
                {applications.filter(a => a.status === 'approved').length}
              </div>
              <div className="text-sm text-gray-600">Schváleno</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="text-3xl font-bold text-red-500">
                {applications.filter(a => a.status === 'rejected').length}
              </div>
              <div className="text-sm text-gray-600">Zamítnuto</div>
            </CardContent>
          </Card>
        </div>

        {/* Detailed Stats for Approved Vendors */}
        {applications.filter(a => a.status === 'approved').length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">📊 Statistiky schválených prodejců</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card>
                <CardContent className="p-6">
                  <div className="text-3xl font-bold text-blue-600">
                    {applications.filter(a => a.status === 'approved' && a.vendorType === 'hlavni-prodej').length}
                  </div>
                  <div className="text-sm text-gray-600">🍔 Hlavní prodejci</div>
                  <div className="text-xs text-gray-500 mt-1">(Food trucky)</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="text-3xl font-bold text-purple-600">
                    {applications.filter(a => a.status === 'approved' && a.vendorType === 'ostatni-prodejci').length}
                  </div>
                  <div className="text-sm text-gray-600">🍭 Ostatní prodejci</div>
                  <div className="text-xs text-gray-500 mt-1">(Sladkosti, výrobky)</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="text-3xl font-bold text-teal-600">
                    {applications.filter(a => a.status === 'approved' && a.eventDays === 'oboudenni').length}
                  </div>
                  <div className="text-sm text-gray-600">🗓️ Dvoudenní</div>
                  <div className="text-xs text-gray-500 mt-1">(Sobota + Neděle)</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="text-3xl font-bold text-amber-600">
                    {applications.filter(a => a.status === 'approved' && a.eventDays === 'sobota-open-stage').length}
                  </div>
                  <div className="text-sm text-gray-600">📅 Jednodenní</div>
                  <div className="text-xs text-gray-500 mt-1">(Pouze sobota)</div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Applications List */}
        <Card>
          <CardHeader>
            <CardTitle>Přihlášky prodejců</CardTitle>
          </CardHeader>
          <CardContent>
            {applications.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p>Zatím žádné přihlášky</p>
              </div>
            ) : (
              <div className="space-y-4">
                {applications.map((app) => (
                  <div
                    key={app.id}
                    className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-slate-900">{app.vendorName}</h3>
                        <p className="text-sm text-gray-500">
                          Přihláška ze dne {new Date(app.createdAt).toLocaleDateString('cs-CZ')}
                        </p>
                        {app.totalPrice && (
                          <p className="text-lg font-bold text-orange-600 mt-1">
                            💰 Částka k fakturaci: {app.totalPrice.toLocaleString('cs-CZ')} Kč bez DPH
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col gap-2 items-end">
                        {getStatusBadge(app.status)}
                        {app.status === 'approved' && getPaymentStatusBadge(app.paymentStatus || 'unpaid')}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="flex items-center gap-2 text-gray-700">
                        <User className="w-4 h-4 text-gray-400" />
                        <span>{app.name}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-700">
                        <Mail className="w-4 h-4 text-gray-400" />
                        <a href={`mailto:${app.email}`} className="hover:text-orange-500">
                          {app.email}
                        </a>
                      </div>
                      <div className="flex items-center gap-2 text-gray-700">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <a href={`tel:${app.phone}`} className="hover:text-orange-500">
                          {app.phone}
                        </a>
                      </div>
                    </div>

                    <div className="flex gap-2 flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSelectedApp(app)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Detail
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-blue-300 text-blue-600 hover:bg-blue-50"
                        onClick={() => openStatusPage(app.id)}
                      >
                        <Link2 className="w-4 h-4 mr-1" />
                        Otevřít status
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-purple-300 text-purple-600 hover:bg-purple-50"
                        onClick={() => copyStatusLink(app.id, app.vendorName)}
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        Kopírovat link
                      </Button>
                      
                      {app.status === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            className="bg-green-500 hover:bg-green-600 text-white"
                            onClick={() => updateStatus(app.id, 'approved')}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Schválit
                          </Button>
                          <Button
                            size="sm"
                            className="bg-red-500 hover:bg-red-600 text-white"
                            onClick={() => updateStatus(app.id, 'rejected')}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Zamítnout
                          </Button>
                        </>
                      )}
                      
                      {app.status === 'approved' && (
                        <>
                          {app.paymentStatus === 'unpaid' ? (
                            <Button
                              size="sm"
                              className="bg-blue-500 hover:bg-blue-600 text-white"
                              onClick={() => updatePaymentStatus(app.id, 'paid')}
                            >
                              💰 Označit jako zaplaceno
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updatePaymentStatus(app.id, 'unpaid')}
                            >
                              ⏳ Označit jako nezaplaceno
                            </Button>
                          )}
                        </>
                      )}
                      
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                        onClick={() => setDeleteConfirm({ id: app.id, name: app.vendorName })}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Smazat
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detail Modal */}
      {selectedApp && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setSelectedApp(null)}>
          <Card className="max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-2xl">{selectedApp.vendorName}</CardTitle>
                  <p className="text-gray-600 mt-1">{selectedApp.name}</p>
                </div>
                <Button variant="ghost" onClick={() => setSelectedApp(null)}>✕</Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Status Link Section */}
              <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4">
                <h4 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
                  <Link2 className="w-5 h-5" />
                  Odkaz na statusovou stránku
                </h4>
                <p className="text-sm text-blue-800 mb-3">
                  Tento link můžete poslat prodejci, aby si zkontroloval stav své přihlášky:
                </p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={`${window.location.origin}/status/${selectedApp.id}`}
                    className="flex-1 px-3 py-2 text-sm border border-blue-300 rounded bg-white font-mono"
                    onClick={(e) => e.target.select()}
                  />
                  <Button
                    size="sm"
                    className="bg-blue-500 hover:bg-blue-600 text-white"
                    onClick={() => copyStatusLink(selectedApp.id, selectedApp.vendorName)}
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Kopírovat
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-blue-300 text-blue-600"
                    onClick={() => openStatusPage(selectedApp.id)}
                  >
                    <Link2 className="w-4 h-4 mr-1" />
                    Otevřít
                  </Button>
                </div>
              </div>

              {/* Document Manager - pouze pro schválené */}
              {selectedApp.status === 'approved' && (
                <DocumentManager 
                  applicationId={selectedApp.id}
                  documents={selectedApp.documents || {}}
                  onUpdate={loadApplications}
                />
              )}

              <div>
                <h4 className="font-bold text-lg mb-3 text-orange-500">Kontaktní údaje</h4>
                <div className="space-y-2">
                  <p><strong>Email:</strong> {selectedApp.email}</p>
                  <p><strong>Telefon:</strong> {selectedApp.phone}</p>
                  <p><strong>WWW/FB:</strong> <a href={selectedApp.website} target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline">{selectedApp.website}</a></p>
                </div>
              </div>

              <div>
                <h4 className="font-bold text-lg mb-3 text-orange-500">Fakturační údaje</h4>
                <p><strong>Plátce DPH:</strong> {selectedApp.vatPayer}</p>
                <pre className="bg-gray-50 p-3 rounded mt-2 text-sm whitespace-pre-wrap">{selectedApp.invoiceDetails}</pre>
              </div>

              <div>
                <h4 className="font-bold text-lg mb-3 text-orange-500">O prodejci</h4>
                <p className="text-gray-700">{selectedApp.description}</p>
              </div>

              <div>
                <h4 className="font-bold text-lg mb-3 text-orange-500">Předpokládané menu</h4>
                <pre className="bg-gray-50 p-3 rounded text-sm whitespace-pre-wrap">{selectedApp.menu}</pre>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <h4 className="font-bold mb-2 text-orange-500">Rozměr stánku</h4>
                  <p>{selectedApp.stallSize}</p>
                </div>
                <div>
                  <h4 className="font-bold mb-2 text-orange-500">Elektrická přípojka</h4>
                  <p>{selectedApp.electricConnection}</p>
                </div>
                {selectedApp.eventDays && (
                  <div>
                    <h4 className="font-bold mb-2 text-orange-500">Dny akce</h4>
                    <p className="font-semibold">
                      {selectedApp.eventDays === 'oboudenni' ? '🗓️ Oboudenní (sobota + neděle)' : '📅 Pouze sobota - Open Stage'}
                    </p>
                  </div>
                )}
                {selectedApp.vendorType && (
                  <div>
                    <h4 className="font-bold mb-2 text-orange-500">Typ prodejce</h4>
                    <p className="font-semibold">
                      {selectedApp.vendorType === 'hlavni-prodej' ? '🍔 Hlavní prodej (Food truck)' : '🍭 Ostatní prodejci'}
                    </p>
                  </div>
                )}
                {selectedApp.totalPrice && (
                  <div>
                    <h4 className="font-bold mb-2 text-orange-500">Částka k fakturaci</h4>
                    <p className="text-xl font-bold text-orange-600">
                      {selectedApp.totalPrice.toLocaleString('cs-CZ')} Kč bez DPH
                    </p>
                  </div>
                )}
                {selectedApp.status === 'approved' && (
                  <div>
                    <h4 className="font-bold mb-2 text-orange-500">Stav platby</h4>
                    {getPaymentStatusBadge(selectedApp.paymentStatus || 'unpaid')}
                  </div>
                )}
              </div>

              {selectedApp.notes && (
                <div>
                  <h4 className="font-bold text-lg mb-3 text-orange-500">Poznámky</h4>
                  <p className="text-gray-700">{selectedApp.notes}</p>
                </div>
              )}

              {/* Galerie fotek */}
              <div>
                <h4 className="font-bold text-lg mb-3 text-orange-500">
                  Fotografie {selectedApp.photos && selectedApp.photos.length > 0 ? `(${selectedApp.photos.length})` : ''}
                </h4>
                
                {selectedApp.photos && selectedApp.photos.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {selectedApp.photos.map((photo, index) => {
                      // Debug log
                      console.log(`Photo ${index + 1}:`, photo);
                      
                      // Zkontroluj, jestli je photo validní URL string
                      const photoUrl = typeof photo === 'string' && photo.trim() !== '' 
                        ? (photo.startsWith('http') ? photo : `${BACKEND_URL}${photo}`)
                        : null;
                      
                      if (!photoUrl) {
                        return (
                          <div key={index} className="w-full h-48 bg-yellow-50 border-2 border-yellow-300 rounded-lg flex flex-col items-center justify-center text-yellow-700 p-4">
                            <p className="font-bold mb-1">⚠️ Foto {index + 1}</p>
                            <p className="text-xs text-center">Chybí URL fotky v databázi</p>
                          </div>
                        );
                      }
                      
                      return (
                        <a
                          key={index}
                          href={photoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                        >
                          <img
                            src={photoUrl}
                            alt={`Foto ${index + 1}`}
                            className="w-full h-48 object-cover rounded-lg border-2 border-gray-200 hover:border-orange-500 transition-colors cursor-pointer"
                            onError={(e) => {
                              console.error(`Chyba načítání fotky ${index + 1}:`, photoUrl);
                              e.target.style.display = 'none';
                              const errorDiv = document.createElement('div');
                              errorDiv.className = 'w-full h-48 bg-red-50 border-2 border-red-300 rounded-lg flex flex-col items-center justify-center text-red-600 p-4';
                              errorDiv.innerHTML = `<p class="font-bold mb-1">❌ Foto ${index + 1}</p><p class="text-xs text-center">Soubor nenalezen na serveru</p>`;
                              e.target.parentElement.appendChild(errorDiv);
                            }}
                            onLoad={() => {
                              console.log(`✅ Fotka ${index + 1} načtena úspěšně`);
                            }}
                          />
                        </a>
                      );
                    })}
                  </div>
                ) : (
                  <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-6 text-center">
                    <p className="text-yellow-800 font-semibold mb-2">
                      ℹ️ Prodejce nenahrál žádné fotografie
                    </p>
                    <p className="text-sm text-yellow-700">
                      Nebo fotografie byly nahrány, ale neuložily se odkazy do databáze.<br/>
                      Kontaktujte prodejce pro opětovné zaslání fotografií.
                    </p>
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4 flex-wrap">
                {selectedApp.status === 'pending' && (
                  <>
                    <Button
                      className="bg-green-500 hover:bg-green-600 text-white flex-1"
                      onClick={() => {
                        updateStatus(selectedApp.id, 'approved');
                        setSelectedApp(null);
                      }}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Schválit
                    </Button>
                    <Button
                      className="bg-red-500 hover:bg-red-600 text-white flex-1"
                      onClick={() => {
                        updateStatus(selectedApp.id, 'rejected');
                        setSelectedApp(null);
                      }}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Zamítnout
                    </Button>
                  </>
                )}
                
                {selectedApp.status === 'approved' && (
                  <>
                    {selectedApp.paymentStatus === 'unpaid' ? (
                      <Button
                        className="bg-blue-500 hover:bg-blue-600 text-white flex-1"
                        onClick={() => {
                          updatePaymentStatus(selectedApp.id, 'paid');
                        }}
                      >
                        💰 Označit jako zaplaceno
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => {
                          updatePaymentStatus(selectedApp.id, 'unpaid');
                        }}
                      >
                        ⏳ Označit jako nezaplaceno
                      </Button>
                    )}
                  </>
                )}
                
                <Button
                  variant="outline"
                  className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                  onClick={() => {
                    setDeleteConfirm({ id: selectedApp.id, name: selectedApp.vendorName });
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Smazat přihlášku
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setDeleteConfirm(null)}>
          <Card className="max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="text-xl text-red-600 flex items-center gap-2">
                <Trash2 className="w-6 h-6" />
                Smazat přihlášku
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-700">
                Opravdu chcete smazat přihlášku prodejce <strong>"{deleteConfirm.name}"</strong>?
              </p>
              <p className="text-sm text-red-600">
                ⚠️ Tato akce je nevratná!
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setDeleteConfirm(null)}
                >
                  Zrušit
                </Button>
                <Button
                  className="bg-red-500 hover:bg-red-600 text-white flex-1"
                  onClick={() => handleDelete(deleteConfirm.id, deleteConfirm.name)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Ano, smazat
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
